import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
    campaignThreadSocketJoin,
    campaignThreadSocketLeave,
    campaignThreadSocketTyping,
} from '../redux/buzzbridge/actions';

/** Join the campaign thread socket room while the workspace discussion is open. */
export function useCampaignThreadSocketRoom(participantId: string | undefined, enabled = true) {
    const dispatch = useDispatch();
    const pid = participantId?.trim() ?? '';

    useEffect(() => {
        if (!enabled || !pid) return;
        dispatch(campaignThreadSocketJoin({ participantId: pid }));
        return () => {
            dispatch(campaignThreadSocketLeave({ participantId: pid }));
        };
    }, [dispatch, enabled, pid]);
}

const TYPING_STOP_IDLE_MS = 3000;
const TYPING_HEARTBEAT_MS = 1200;

/** Debounced emit for campaignThread:typing — call from composer onChange. */
export function useCampaignThreadTypingEmitter(participantId: string | undefined, enabled = true) {
    const dispatch = useDispatch();
    const pid = participantId?.trim() ?? '';
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isTypingRef = useRef(false);

    const stopHeartbeat = () => {
        if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }
    };

    const emitStopped = (force = false) => {
        stopHeartbeat();
        if (timerRef.current) clearTimeout(timerRef.current);
        if (pid && (isTypingRef.current || force)) {
            isTypingRef.current = false;
            dispatch(campaignThreadSocketTyping({ participantId: pid, typing: false }));
        }
    };

    useEffect(() => {
        return () => {
            emitStopped();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, pid]);

    return (hasDraft: boolean) => {
        if (!enabled || !pid) return;
        if (hasDraft) {
            if (!isTypingRef.current) {
                isTypingRef.current = true;
                dispatch(campaignThreadSocketTyping({ participantId: pid, typing: true }));
                stopHeartbeat();
                heartbeatRef.current = setInterval(() => {
                    if (isTypingRef.current && pid) {
                        dispatch(campaignThreadSocketTyping({ participantId: pid, typing: true }));
                    }
                }, TYPING_HEARTBEAT_MS);
            }
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(emitStopped, TYPING_STOP_IDLE_MS);
            return;
        }
        emitStopped(true);
    };
}
